// Datanodes catalog — drives the Networks reference (/networks) and the
// "Networks" section on the landing page.
//
// Source of truth for slugs, routing and calling conventions is the Datanodes
// gateway config (datanodes-deployment / api-gateway). Only public hostnames
// appear here — never upstream node addresses.
//
// Examples use the `{{KEY}}` token. At render time it is replaced either by
// the reader's own key placeholder or by the shared public demo key, which is
// NOT stored in this repo — it is resolved at runtime from the published Sui
// API reference article (see src/lib/demoKey.js).

export const KEY_TOKEN = "{{KEY}}";
export const KEY_PLACEHOLDER = "YOUR_API_KEY";

export const GATEWAY = {
  rpc: "https://rpc.datanodes.nodefleet.net",
  graphql: "https://rpc.datanodes.nodefleet.net/graphql",
  ws: "wss://ws.datanodes.nodefleet.net",
  suiGrpc: "sui-grpc.datanodes.nodefleet.net:443",
  solanaGrpc: "solana-grpc.datanodes.nodefleet.net:443",
  dashboard: "https://datanodes.nodefleet.net/",
  contact: "mailto:nodes@nodefleet.net",
};

export const UPDATED = "August 2026";

// Replace the key token in any string with the active key.
export const withKey = (text, key) =>
  typeof text === "string" ? text.split(KEY_TOKEN).join(key || KEY_PLACEHOLDER) : text;

// ---------------------------------------------------------------------------
// Chain families — each family shares a calling convention and a method set.
// ---------------------------------------------------------------------------
export const FAMILIES = {
  evm: {
    id: "evm",
    name: "EVM · JSON-RPC",
    short: "EVM",
    accent: "#99DFAF",
    blurb:
      "Standard Ethereum JSON-RPC (eth_*, net_*, web3_*) for every EVM network. Same request shape everywhere — only the x-chain slug changes.",
    interface: "JSON-RPC over HTTPS",
    protocol: "JSON-RPC",
    transport: "POST / with a JSON-RPC 2.0 body",
  },
  solana: {
    id: "solana",
    name: "Solana · JSON-RPC + gRPC",
    short: "Solana",
    accent: "#5AA9E6",
    blurb:
      "Solana JSON-RPC for reads and transaction submission, plus a paid Yellowstone gRPC stream for real-time account / slot / transaction updates.",
    interface: "JSON-RPC over HTTPS · gRPC (paid streams)",
    protocol: "JSON-RPC · gRPC",
    transport: "POST / with a JSON-RPC 2.0 body",
  },
  cosmos: {
    id: "cosmos",
    name: "Cosmos · Tendermint RPC",
    short: "Cosmos RPC",
    accent: "#B7E8C8",
    blurb:
      "CometBFT / Tendermint JSON-RPC for Cosmos-SDK chains — status, blocks, ABCI queries and transaction broadcast.",
    interface: "Tendermint JSON-RPC over HTTPS",
    protocol: "Tendermint RPC",
    transport: "POST / with a JSON-RPC 2.0 body",
  },
  "cosmos-rest": {
    id: "cosmos-rest",
    name: "Cosmos · REST (LCD)",
    short: "Cosmos REST",
    accent: "#B7E8C8",
    blurb:
      "The Cosmos-SDK gRPC-gateway REST API (/cosmos/*). Send the module path after the gateway host; the x-chain slug ends in -rest.",
    interface: "REST over HTTPS",
    protocol: "REST (LCD)",
    transport: "GET /cosmos/<module>/…",
  },
  sui: {
    id: "sui",
    name: "Sui · GraphQL + gRPC",
    short: "Sui",
    accent: "#5AA9E6",
    blurb:
      "Sui's modern query surfaces — GraphQL for shaped reads, gRPC for low-latency ledger access, execution and checkpoint streaming. Legacy sui_* JSON-RPC is still routed for migration.",
    interface: "GraphQL · gRPC · JSON-RPC (legacy)",
    protocol: "GraphQL · gRPC",
    transport: "POST /graphql · HTTP/2 gRPC · POST /",
  },
  near: {
    id: "near",
    name: "NEAR · JSON-RPC",
    short: "NEAR",
    accent: "#99DFAF",
    blurb: "NEAR Protocol JSON-RPC — status, blocks, chunks, view calls and transaction status.",
    interface: "JSON-RPC over HTTPS",
    protocol: "JSON-RPC",
    transport: "POST / with a JSON-RPC 2.0 body",
  },
  filecoin: {
    id: "filecoin",
    name: "Filecoin · Lotus JSON-RPC",
    short: "Filecoin",
    accent: "#5AA9E6",
    blurb:
      "Lotus JSON-RPC (Filecoin.*) for chain, state and message-pool queries; FEVM eth_* methods are served by the same node.",
    interface: "JSON-RPC over HTTPS",
    protocol: "Lotus JSON-RPC",
    transport: "POST / with a JSON-RPC 2.0 body",
  },
  iota: {
    id: "iota",
    name: "IOTA · JSON-RPC",
    short: "IOTA",
    accent: "#B7E8C8",
    blurb: "IOTA Rebased JSON-RPC (iota_* / iotax_*) — objects, balances, checkpoints and transaction execution.",
    interface: "JSON-RPC over HTTPS",
    protocol: "JSON-RPC",
    transport: "POST / with a JSON-RPC 2.0 body",
  },
  canopy: {
    id: "canopy",
    name: "Canopy · native REST + EVM",
    short: "Canopy",
    accent: "#99DFAF",
    blurb:
      "Canopy's native /v1 API is reachable under /rpc/v1/* (queries) and /adminrpc/v1/* (tx). POSTing a JSON-RPC body to / hits the chain's EVM endpoint.",
    interface: "Native REST · EVM JSON-RPC",
    protocol: "Native REST · EVM",
    transport: "POST /rpc/v1/<route> · POST /",
  },
  other: {
    id: "other",
    name: "Other networks",
    short: "Other",
    accent: "#B7E8C8",
    blurb:
      "Routed through the same gateway with the same headers. Use the chain's native RPC method set; see the chain's own documentation for method names.",
    interface: "Native RPC over HTTPS",
    protocol: "Native RPC",
    transport: "POST / (forwarded to the chain node)",
  },
};

export const FAMILY_ORDER = ["evm", "solana", "sui", "cosmos", "cosmos-rest", "near", "filecoin", "iota", "canopy", "other"];

// ---------------------------------------------------------------------------
// Chain catalog — slug = value of the x-chain header (or path segment).
// logo: local asset path or a remote icon URL; missing → monogram fallback.
// chainId: only where it is a well-known, stable value. Omit rather than guess.
// net: optional override of the network bucket inside the ecosystem group
//      ({ key, label }). Default bucket = "mainnet" or the testnet name taken
//      from the chain name (Sepolia / Goerli / Testnet), see NETWORKS below.
// ---------------------------------------------------------------------------
const llama = (s) => `https://icons.llamao.fi/icons/chains/rsz_${s}.jpg`;

export const CHAINS = [
  // ----- EVM mainnets -----
  { slug: "eth", name: "Ethereum", family: "evm", network: "mainnet", chainId: 1, aliases: ["eth-mainnet", "ethereum"], logo: "/assets/icons/chains/ethereum.png", ws: "eth-mainnet", featured: true },
  { slug: "base", name: "Base", family: "evm", network: "mainnet", chainId: 8453, aliases: ["base-mainnet"], logo: "/assets/icons/chains/base.png", featured: true },
  { slug: "arbitrum", name: "Arbitrum One", family: "evm", network: "mainnet", chainId: 42161, logo: "/assets/icons/chains/arbitrum.png", featured: true },
  { slug: "optimism", name: "OP Mainnet", family: "evm", network: "mainnet", chainId: 10, aliases: ["optimism-mainnet"], logo: "/assets/icons/chains/optimism.png" },
  { slug: "polygon", name: "Polygon PoS", family: "evm", network: "mainnet", chainId: 137, aliases: ["matic"], logo: "/assets/icons/chains/polygon.png", featured: true },
  { slug: "bsc", name: "BNB Smart Chain", family: "evm", network: "mainnet", chainId: 56, aliases: ["bsc-mainnet"], logo: "/assets/icons/chains/bsc.png", ws: "bsc-mainnet" },
  { slug: "avax", name: "Avalanche C-Chain", family: "evm", network: "mainnet", chainId: 43114, aliases: ["avax-mainnet"], logo: "/assets/icons/chains/avax.png" },
  { slug: "fantom", name: "Fantom Opera", family: "evm", network: "mainnet", chainId: 250, logo: "/assets/icons/chains/fantom.png" },
  { slug: "linea", name: "Linea", family: "evm", network: "mainnet", chainId: 59144, logo: "/assets/icons/linea-logo.png" },
  { slug: "mantle", name: "Mantle", family: "evm", network: "mainnet", chainId: 5000, logo: "/assets/icons/mantle-logo.png" },
  { slug: "metis", name: "Metis Andromeda", family: "evm", network: "mainnet", chainId: 1088, aliases: ["metis-mainnet"], logo: llama("metis") },
  { slug: "xdai", name: "Gnosis Chain", family: "evm", network: "mainnet", chainId: 100, logo: "/assets/icons/chains/gnosis.png" },
  { slug: "evmos", name: "Evmos", family: "evm", network: "mainnet", chainId: 9001, logo: "/assets/icons/chains/evmos.png" },
  { slug: "fuse", name: "Fuse", family: "evm", network: "mainnet", chainId: 122, logo: "/assets/icons/chains/fuse.png" },
  { slug: "klaytn", name: "Kaia (Klaytn)", family: "evm", network: "mainnet", chainId: 8217, logo: "/assets/icons/chains/klaytn.png" },
  { slug: "harmony", name: "Harmony", family: "evm", network: "mainnet", chainId: 1666600000, logo: llama("harmony") },
  { slug: "moonriver", name: "Moonriver", family: "evm", network: "mainnet", chainId: 1285, logo: llama("moonriver") },
  { slug: "boba", name: "Boba Network", family: "evm", network: "mainnet", chainId: 288, logo: llama("boba") },
  { slug: "iotex", name: "IoTeX", family: "evm", network: "mainnet", chainId: 4689, logo: llama("iotex") },
  { slug: "oasys", name: "Oasys", family: "evm", network: "mainnet", chainId: 248, logo: llama("oasys") },
  { slug: "opbnb", name: "opBNB", family: "evm", network: "mainnet", chainId: 204, logo: llama("op_bnb") },
  { slug: "unichain", name: "Unichain", family: "evm", network: "mainnet", chainId: 130, logo: llama("unichain") },
  { slug: "ink", name: "Ink", family: "evm", network: "mainnet", chainId: 57073, logo: llama("ink") },
  { slug: "apechain", name: "ApeChain", family: "evm", network: "mainnet", chainId: 33139, logo: llama("apechain") },
  { slug: "xlayer", name: "X Layer", family: "evm", network: "mainnet", chainId: 196, logo: llama("xlayer") },
  { slug: "hyperliquid-evm", name: "Hyperliquid EVM", family: "evm", network: "mainnet", chainId: 999, logo: llama("hyperliquid"), featured: true },
  { slug: "story-mainnet", name: "Story", family: "evm", network: "mainnet", chainId: 1514, logo: "/assets/icons/story-logo.png", ws: "story-mainnet" },
  { slug: "frax-mainnet", name: "Fraxtal", family: "evm", network: "mainnet", chainId: 252, logo: llama("fraxtal"), ws: "frax-mainnet" },
  { slug: "plasma-mainnet", name: "Plasma", family: "evm", network: "mainnet", logo: llama("plasma") },
  { slug: "robinhood-mainnet", name: "Robinhood Chain", family: "evm", network: "mainnet", logo: null },
  { slug: "kite-mainnet", name: "Kite", family: "evm", network: "mainnet", logo: null },
  { slug: "campus", name: "Campus", family: "evm", network: "mainnet", logo: null },
  // ----- EVM testnets -----
  { slug: "eth-sepolia", name: "Ethereum Sepolia", family: "evm", network: "testnet", chainId: 11155111, aliases: ["sepolia"], logo: "/assets/icons/chains/ethereum.png" },
  { slug: "goerli", name: "Ethereum Goerli", family: "evm", network: "testnet", chainId: 5, logo: "/assets/icons/chains/ethereum.png" },
  { slug: "arbitrum-sepolia", name: "Arbitrum Sepolia", family: "evm", network: "testnet", chainId: 421614, logo: "/assets/icons/chains/arbitrum.png" },
  { slug: "base-sepolia", name: "Base Sepolia", family: "evm", network: "testnet", chainId: 84532, logo: "/assets/icons/chains/base.png" },
  { slug: "blast-sepolia", name: "Blast Sepolia", family: "evm", network: "testnet", chainId: 168587773, logo: llama("blast") },
  { slug: "mantle-sepolia", name: "Mantle Sepolia", family: "evm", network: "testnet", chainId: 5003, logo: "/assets/icons/mantle-logo.png" },
  { slug: "unichain-sepolia", name: "Unichain Sepolia", family: "evm", network: "testnet", chainId: 1301, logo: llama("unichain") },
  { slug: "hemi-testnet", name: "Hemi Testnet", family: "evm", network: "testnet", chainId: 743111, logo: llama("hemi") },
  { slug: "giwa-sepolia", name: "GIWA Sepolia", family: "evm", network: "testnet", logo: null },
  // ----- Solana -----
  { slug: "solana", name: "Solana", family: "solana", network: "mainnet", logo: "/assets/icons/chains/solana.png", featured: true },
  // ----- Sui -----
  { slug: "sui", name: "Sui", family: "sui", network: "mainnet", logo: "/assets/icons/sui.png", featured: true, dedicated: true },
  // ----- Cosmos (Tendermint RPC) -----
  { slug: "osmosis", name: "Osmosis", family: "cosmos", network: "mainnet", chainId: "osmosis-1", logo: llama("osmosis"), featured: true },
  { slug: "akash-mainnet", name: "Akash", family: "cosmos", network: "mainnet", chainId: "akashnet-2", logo: llama("akash") },
  { slug: "axelar-mainnet", name: "Axelar", family: "cosmos", network: "mainnet", chainId: "axelar-dojo-1", logo: llama("axelar") },
  { slug: "noble", name: "Noble", family: "cosmos", network: "mainnet", chainId: "noble-1", logo: llama("noble") },
  { slug: "injective", name: "Injective", family: "cosmos", network: "mainnet", chainId: "injective-1", logo: llama("injective") },
  { slug: "lava-mainnet", name: "Lava", family: "cosmos", network: "mainnet", chainId: "lava-mainnet-1", logo: "/assets/icons/lava-logo.png" },
  { slug: "shannon-mainnet", name: "Pocket Network (Shannon)", family: "cosmos", network: "mainnet", chainId: "pocket", logo: "/assets/icons/pokt-symbol.svg" },
  { slug: "pasg-mainnet", name: "Passage", family: "cosmos", network: "mainnet", logo: "/assets/icons/passage-logo.svg" },
  // ----- Cosmos REST -----
  { slug: "osmosis-rest", name: "Osmosis REST", family: "cosmos-rest", network: "mainnet", logo: llama("osmosis") },
  { slug: "cosmoshub-rest", name: "Cosmos Hub REST", family: "cosmos-rest", network: "mainnet", logo: llama("cosmos") },
  { slug: "akash-rest", name: "Akash REST", family: "cosmos-rest", network: "mainnet", aliases: ["akash-mainnet-rest"], logo: llama("akash") },
  { slug: "axelar-rest", name: "Axelar REST", family: "cosmos-rest", network: "mainnet", aliases: ["axelar-mainnet-rest"], logo: llama("axelar") },
  { slug: "noble-rest", name: "Noble REST", family: "cosmos-rest", network: "mainnet", logo: llama("noble") },
  { slug: "injective-rest", name: "Injective REST", family: "cosmos-rest", network: "mainnet", logo: llama("injective") },
  { slug: "lava-rest", name: "Lava REST", family: "cosmos-rest", network: "mainnet", aliases: ["lava-mainnet-rest"], logo: "/assets/icons/lava-logo.png" },
  // ----- Others -----
  { slug: "near", name: "NEAR", family: "near", network: "mainnet", chainId: "mainnet", logo: llama("near") },
  { slug: "file-coin", name: "Filecoin", family: "filecoin", network: "mainnet", chainId: 314, logo: llama("filecoin") },
  { slug: "iota", name: "IOTA", family: "iota", network: "mainnet", logo: llama("iota") },
  { slug: "canopy", name: "Canopy", family: "canopy", network: "mainnet", logo: "/assets/icons/canopy-symbol.png" },
  { slug: "canopy-2", name: "Canopy Chain 2", family: "canopy", network: "mainnet", chainId: "0x100000002", logo: "/assets/icons/canopy-symbol.png", net: { key: "chain-2", label: "Chain 2" } },
  { slug: "sei", name: "Sei", family: "other", network: "mainnet", logo: llama("sei") },
  { slug: "pocket", name: "Pocket Network (Morse)", family: "other", network: "mainnet", logo: "/assets/icons/pokt-symbol.svg", net: { key: "morse", label: "Morse" } },
];

export const getChain = (slug) =>
  CHAINS.find((c) => c.slug === slug || (c.aliases || []).includes(slug)) || null;

// ---------------------------------------------------------------------------
// Network groups — one entry per ecosystem for the sidebar network selector.
// A group bundles every slug that belongs to the same chain (mainnet, testnets,
// REST / RPC variants). Slugs not listed here form a group of their own.
// ---------------------------------------------------------------------------
const GROUP_OF = {
  eth: "Ethereum", "eth-sepolia": "Ethereum", goerli: "Ethereum",
  base: "Base", "base-sepolia": "Base",
  arbitrum: "Arbitrum", "arbitrum-sepolia": "Arbitrum",
  optimism: "Optimism", polygon: "Polygon", bsc: "BNB Smart Chain", avax: "Avalanche",
  fantom: "Fantom", linea: "Linea",
  mantle: "Mantle", "mantle-sepolia": "Mantle",
  metis: "Metis", xdai: "Gnosis", evmos: "Evmos", fuse: "Fuse", klaytn: "Kaia", harmony: "Harmony",
  moonriver: "Moonriver", boba: "Boba", iotex: "IoTeX", oasys: "Oasys", opbnb: "opBNB",
  unichain: "Unichain", "unichain-sepolia": "Unichain",
  ink: "Ink", apechain: "ApeChain", xlayer: "X Layer", "hyperliquid-evm": "Hyperliquid",
  "story-mainnet": "Story", "frax-mainnet": "Fraxtal", "plasma-mainnet": "Plasma",
  "robinhood-mainnet": "Robinhood Chain", "kite-mainnet": "Kite", campus: "Campus",
  "blast-sepolia": "Blast", "hemi-testnet": "Hemi", "giwa-sepolia": "GIWA",
  solana: "Solana", sui: "Sui",
  osmosis: "Osmosis", "osmosis-rest": "Osmosis",
  "akash-mainnet": "Akash", "akash-rest": "Akash",
  "axelar-mainnet": "Axelar", "axelar-rest": "Axelar",
  noble: "Noble", "noble-rest": "Noble",
  injective: "Injective", "injective-rest": "Injective",
  "lava-mainnet": "Lava", "lava-rest": "Lava",
  "shannon-mainnet": "Pocket Network", pocket: "Pocket Network",
  "pasg-mainnet": "Passage", "cosmoshub-rest": "Cosmos Hub",
  near: "NEAR", "file-coin": "Filecoin", iota: "IOTA",
  canopy: "Canopy", "canopy-2": "Canopy", sei: "Sei",
};

const groupId = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const NETWORK_GROUPS = (() => {
  const map = new Map();
  CHAINS.forEach((c) => {
    const name = GROUP_OF[c.slug] || c.name;
    const id = groupId(name);
    if (!map.has(id)) map.set(id, { id, name, logo: c.logo, family: c.family, chains: [] });
    const g = map.get(id);
    g.chains.push(c);
    if (!g.logo && c.logo) g.logo = c.logo;
  });
  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
})();

export const groupForChain = (chain) =>
  chain ? NETWORK_GROUPS.find((g) => g.chains.some((c) => c.slug === chain.slug)) || null : null;

// ---------------------------------------------------------------------------
// Networks — one entry per chain id inside an ecosystem group (Ethereum →
// Mainnet / Sepolia / Goerli; Akash → Mainnet). Every Datanodes slug that
// serves the same network (Tendermint RPC + REST, JSON-RPC + WebSocket, …) is an
// *implementation* of that network, not a network of its own.
// The canonical route slug of a network is the slug of its first implementation
// (`/networks/akash-mainnet`); the other implementation slugs resolve to it.
// ---------------------------------------------------------------------------
const testnetLabel = (chain) => {
  const m = chain.name.match(/(sepolia|goerli|holesky|devnet|testnet)/i);
  return m ? m[1][0].toUpperCase() + m[1].slice(1).toLowerCase() : "Testnet";
};
const netKeyOf = (chain) => chain.net?.key || (chain.network === "testnet" ? testnetLabel(chain).toLowerCase() : "mainnet");
const netLabelOf = (chain) => chain.net?.label || (chain.network === "testnet" ? testnetLabel(chain) : "Mainnet");

export const NETWORKS = (() => {
  const all = [];
  NETWORK_GROUPS.forEach((g) => {
    const buckets = new Map();
    g.chains.forEach((c) => {
      const key = netKeyOf(c);
      if (!buckets.has(key)) {
        const label = netLabelOf(c);
        buckets.set(key, {
          id: `${g.id}/${key}`,
          slug: c.slug,
          key,
          label,
          name: g.name,
          fullName: `${g.name} ${label}`,
          env: c.network,
          family: c.family,
          logo: c.logo,
          groupId: g.id,
          chainId: c.chainId,
          chains: [],
        });
      }
      const n = buckets.get(key);
      n.chains.push(c);
      if (n.chainId === undefined && c.chainId !== undefined) n.chainId = c.chainId;
      if (!n.logo && c.logo) n.logo = c.logo;
    });
    // Mainnet first, then the remaining buckets in catalog order.
    g.networks = [...buckets.values()].sort((a, b) => (a.env === b.env ? 0 : a.env === "mainnet" ? -1 : 1));
    all.push(...g.networks);
  });
  return all;
})();

export const groupOfNetwork = (network) => (network ? NETWORK_GROUPS.find((g) => g.id === network.groupId) || null : null);

export const networkForChain = (chain) =>
  chain ? NETWORKS.find((n) => n.chains.some((c) => c.slug === chain.slug)) || null : null;

// Resolve a route slug — a network slug, an implementation slug or an alias —
// to { network, chain }. `chain` is the implementation the slug pointed at.
export const getNetwork = (slug) => {
  const chain = getChain(slug);
  const network = networkForChain(chain);
  return network ? { network, chain } : null;
};

export const networksByFamily = (familyId) => NETWORKS.filter((n) => n.family === familyId);

export const chainsByFamily = (familyId) => CHAINS.filter((c) => c.family === familyId);

export const FEATURED_CHAINS = CHAINS.filter((c) => c.featured);

export const STATS = {
  chains: CHAINS.length,
  networks: NETWORKS.length,
  mainnets: NETWORKS.filter((n) => n.env === "mainnet").length,
  interfaces: ["JSON-RPC", "GraphQL", "gRPC", "REST", "WebSocket"],
};

// ---------------------------------------------------------------------------
// Calling conventions — header form and path form are equivalent.
// ---------------------------------------------------------------------------
export const HEADERS = [
  { name: "x-api-key", value: "sk_live_…", note: "Your Datanodes key. Required on every request." },
  { name: "x-chain", value: "<slug>", note: "Selects the network. Not needed for gRPC — the service path selects the chain." },
  { name: "Content-Type", value: "application/json", note: "For JSON-RPC / GraphQL bodies." },
];

export const URL_FORMS = [
  { label: "Header form", pattern: `${GATEWAY.rpc}/`, note: "Send x-api-key + x-chain headers." },
  { label: "Path form", pattern: `${GATEWAY.rpc}/<chain>/<sk_live_key>/`, note: "Credentials in the URL — drop-in for tools that only take a URL." },
  { label: "Path form (v1)", pattern: `${GATEWAY.rpc}/v1/<chain>/<sk_live_key>/`, note: "Same as above." },
  { label: "Path form + tail", pattern: `${GATEWAY.rpc}/<chain>/<sk_live_key>/<upstream path>`, note: "REST chains: the tail is forwarded upstream, e.g. /osmosis-rest/<key>/cosmos/staking/v1beta1/pool" },
];

// ---------------------------------------------------------------------------
// Examples — request/response pairs per family. `slug` is substituted per chain.
// ---------------------------------------------------------------------------
export const buildCurl = ({ slug, body, path = "/", method = "POST" }) => {
  const lines = [`curl -s -X ${method} ${GATEWAY.rpc}${path} \\`, `  -H "x-api-key: ${KEY_TOKEN}" \\`, `  -H "x-chain: ${slug}"`];
  if (body) {
    lines[2] += " \\";
    lines.push(`  -H "Content-Type: application/json" \\`);
    lines.push(`  -d '${body}'`);
  }
  return lines.join("\n");
};

export const EXAMPLES = {
  evm: [
    {
      title: "Chain id",
      body: '{"jsonrpc":"2.0","id":1,"method":"eth_chainId","params":[]}',
      response: '{ "jsonrpc": "2.0", "id": 1, "result": "0x1" }',
      note: "Response captured on Ethereum mainnet (0x1 = chain 1).",
    },
    {
      title: "Latest block number",
      body: '{"jsonrpc":"2.0","id":1,"method":"eth_blockNumber","params":[]}',
      response: '{ "jsonrpc": "2.0", "id": 1, "result": "0x189a851" }',
      note: "Hex block height — 0x189a851 = 25,798,737.",
    },
    {
      title: "Account balance",
      body: '{"jsonrpc":"2.0","id":1,"method":"eth_getBalance","params":["0x00000000219ab540356cBB839Cbe05303d7705Fa","latest"]}',
      response: '{ "jsonrpc": "2.0", "id": 1, "result": "0x4a4b7aa821eb84cc6fac86" }',
      note: "Balance in wei (hex). The address is the Beacon deposit contract.",
    },
    {
      title: "Batch — several calls in one request",
      body: '[{"jsonrpc":"2.0","id":1,"method":"eth_chainId","params":[]},{"jsonrpc":"2.0","id":2,"method":"eth_gasPrice","params":[]}]',
      response: '[ { "jsonrpc": "2.0", "id": 1, "result": "0x1" }, { "jsonrpc": "2.0", "id": 2, "result": "0x…" } ]',
      note: "Send a JSON array; you get an array back in the same order. Each call counts toward your quota.",
    },
  ],
  solana: [
    {
      title: "Current slot",
      body: '{"jsonrpc":"2.0","id":1,"method":"getSlot"}',
      response: '{ "jsonrpc": "2.0", "result": 440542921, "id": 1 }',
    },
    {
      title: "Node version",
      body: '{"jsonrpc":"2.0","id":1,"method":"getVersion"}',
      response: '{ "jsonrpc": "2.0", "result": { "feature-set": 565236538, "solana-core": "4.2.0" }, "id": 1 }',
    },
    {
      title: "Account balance (lamports)",
      body: '{"jsonrpc":"2.0","id":1,"method":"getBalance","params":["Vote111111111111111111111111111111111111111"]}',
      response: '{ "jsonrpc": "2.0", "result": { "context": { "apiVersion": "4.2.0", "slot": 440542923 }, "value": 1 }, "id": 1 }',
    },
  ],
  cosmos: [
    {
      title: "Node status",
      body: '{"jsonrpc":"2.0","id":1,"method":"status","params":[]}',
      response:
        '{ "jsonrpc": "2.0", "id": 1, "result": {\n    "node_info": { "network": "osmosis-1", "version": "0.38.19", … },\n    "sync_info": { "latest_block_height": "68921960",\n                   "latest_block_time": "2026-08-20T20:33:49Z",\n                   "catching_up": false }, … } }',
      note: "Captured on Osmosis. `network` is the Cosmos chain-id.",
    },
    {
      title: "Block by height",
      body: '{"jsonrpc":"2.0","id":1,"method":"block","params":{"height":"68921960"}}',
      response: '{ "jsonrpc": "2.0", "id": 1, "result": { "block_id": { … }, "block": { "header": { … }, "data": { "txs": [ … ] } } } }',
    },
  ],
  "cosmos-rest": [
    {
      title: "Node info",
      method: "GET",
      path: "/cosmos/base/tendermint/v1beta1/node_info",
      response:
        '{ "default_node_info": { "network": "osmosis-1", "version": "0.38.22", … },\n  "application_version": { "name": "osmosis", "app_name": "osmosisd", "version": "31.0.2", … } }',
      note: "Captured on Osmosis REST.",
    },
    {
      title: "Latest block",
      method: "GET",
      path: "/cosmos/base/tendermint/v1beta1/blocks/latest",
      response: '{ "block_id": { … }, "block": { "header": { "chain_id": "osmosis-1", "height": "…", … } } }',
    },
    {
      title: "Bank balances",
      method: "GET",
      path: "/cosmos/bank/v1beta1/balances/<bech32-address>",
      response: '{ "balances": [ { "denom": "uosmo", "amount": "…" } ], "pagination": { … } }',
    },
  ],
  near: [
    {
      title: "Node status",
      body: '{"jsonrpc":"2.0","id":"1","method":"status","params":[]}',
      response:
        '{ "jsonrpc": "2.0", "id": "1", "result": {\n    "chain_id": "mainnet", "protocol_version": 86,\n    "sync_info": { "latest_block_height": 212196217,\n                   "latest_block_time": "2026-08-20T20:33:53Z", … }, … } }',
    },
    {
      title: "View account",
      body: '{"jsonrpc":"2.0","id":"1","method":"query","params":{"request_type":"view_account","finality":"final","account_id":"near"}}',
      response: '{ "jsonrpc": "2.0", "id": "1", "result": { "amount": "…", "locked": "0", "storage_usage": …, "block_height": … } }',
    },
  ],
  filecoin: [
    {
      title: "Chain head",
      body: '{"jsonrpc":"2.0","id":1,"method":"Filecoin.ChainHead","params":[]}',
      response: '{ "jsonrpc": "2.0", "id": 1, "result": { "Cids": [ { "/": "bafy…" } ], "Blocks": [ … ], "Height": … } }',
    },
    {
      title: "FEVM — chain id",
      body: '{"jsonrpc":"2.0","id":1,"method":"eth_chainId","params":[]}',
      response: '{ "jsonrpc": "2.0", "id": 1, "result": "0x13a" }',
      note: "0x13a = 314, the Filecoin mainnet EVM chain id.",
    },
  ],
  iota: [
    {
      title: "Chain identifier",
      body: '{"jsonrpc":"2.0","id":1,"method":"iota_getChainIdentifier","params":[]}',
      response: '{ "jsonrpc": "2.0", "id": 1, "result": "…" }',
    },
    {
      title: "Latest checkpoint",
      body: '{"jsonrpc":"2.0","id":1,"method":"iota_getLatestCheckpointSequenceNumber","params":[]}',
      response: '{ "jsonrpc": "2.0", "id": 1, "result": "…" }',
    },
  ],
  canopy: [
    {
      title: "Native query — chain height",
      path: "/rpc/v1/query/height",
      body: "{}",
      response: '{ "height": … }',
      note: "/rpc/v1/<route> is mapped to the node's native /v1/<route> tree.",
    },
    {
      title: "EVM — chain id",
      body: '{"jsonrpc":"2.0","id":1,"method":"eth_chainId","params":[]}',
      response: '{ "jsonrpc": "2.0", "id": 1, "result": "0x100000002" }',
      note: "A JSON-RPC POST to / is routed to the chain's EVM endpoint. Shown for canopy-2.",
    },
  ],
  other: [
    {
      title: "Forwarded RPC call",
      body: '{"jsonrpc":"2.0","id":1,"method":"<native method>","params":[]}',
      response: "{ … chain node response, passed through unchanged … }",
    },
  ],
};

// ---------------------------------------------------------------------------
// Method catalogs per family — grouped for the reference tables.
// EVM/Solana/Cosmos/NEAR/Filecoin lists are the standard public method sets the
// upstream nodes serve; debug_/trace_ style namespaces vary by node type.
// ---------------------------------------------------------------------------
export const METHODS = {
  evm: [
    {
      group: "Chain state",
      rows: [
        ["eth_chainId", "Chain id (hex)."],
        ["eth_blockNumber", "Latest block height (hex)."],
        ["eth_gasPrice", "Current gas price suggestion (wei)."],
        ["eth_maxPriorityFeePerGas", "Suggested priority fee (EIP-1559)."],
        ["eth_feeHistory", "Base fee and priority-fee percentiles for a block range."],
        ["eth_syncing", "Sync status, or false when fully synced."],
        ["net_version", "Network id (decimal string)."],
        ["web3_clientVersion", "Node client and version."],
      ],
    },
    {
      group: "Blocks & transactions",
      rows: [
        ["eth_getBlockByNumber", "Block by number / tag (latest, safe, finalized)."],
        ["eth_getBlockByHash", "Block by hash."],
        ["eth_getBlockTransactionCountByNumber", "Tx count in a block."],
        ["eth_getTransactionByHash", "Transaction by hash."],
        ["eth_getTransactionReceipt", "Receipt — status, logs, gas used."],
        ["eth_getTransactionByBlockNumberAndIndex", "Transaction by block + index."],
        ["eth_getUncleByBlockNumberAndIndex", "Uncle / ommer by block + index."],
      ],
    },
    {
      group: "Accounts & contracts",
      rows: [
        ["eth_getBalance", "Balance in wei at a block tag."],
        ["eth_getTransactionCount", "Account nonce."],
        ["eth_getCode", "Contract bytecode."],
        ["eth_getStorageAt", "Raw storage slot."],
        ["eth_call", "Execute a read-only contract call."],
        ["eth_estimateGas", "Gas estimate for a transaction."],
        ["eth_createAccessList", "Access list for a call (EIP-2930)."],
      ],
    },
    {
      group: "Logs & write",
      rows: [
        ["eth_getLogs", "Filtered event logs (address / topics / block range)."],
        ["eth_sendRawTransaction", "Broadcast a signed transaction."],
        ["eth_newFilter / eth_getFilterChanges", "Polling filters (where the node supports them)."],
      ],
    },
  ],
  solana: [
    {
      group: "Cluster & blocks",
      rows: [
        ["getSlot", "Current slot."],
        ["getBlockHeight", "Current block height."],
        ["getLatestBlockhash", "Recent blockhash + last valid block height."],
        ["getBlock", "Block by slot (transactions, rewards)."],
        ["getBlocks / getBlocksWithLimit", "Confirmed slots in a range."],
        ["getEpochInfo", "Epoch, slot index, slots in epoch."],
        ["getVersion", "solana-core version + feature set."],
        ["getHealth", "Node health."],
      ],
    },
    {
      group: "Accounts & tokens",
      rows: [
        ["getBalance", "Lamport balance."],
        ["getAccountInfo", "Account data (base64 / jsonParsed)."],
        ["getMultipleAccounts", "Batch account lookup."],
        ["getProgramAccounts", "Accounts owned by a program (with filters)."],
        ["getTokenAccountBalance", "SPL token account balance."],
        ["getTokenAccountsByOwner", "SPL token accounts for an owner."],
        ["getTokenSupply", "Mint supply."],
      ],
    },
    {
      group: "Transactions",
      rows: [
        ["getTransaction", "Transaction by signature."],
        ["getSignaturesForAddress", "Signature history for an address."],
        ["getSignatureStatuses", "Confirmation status for signatures."],
        ["sendTransaction", "Submit a signed transaction."],
        ["simulateTransaction", "Simulate without submitting."],
        ["getFeeForMessage", "Fee for a compiled message."],
      ],
    },
    {
      group: "gRPC (paid add-on)",
      rows: [
        ["geyser.Geyser/Subscribe", "Yellowstone stream — accounts, slots, transactions, blocks with server-side filters."],
        ["geyser.Geyser/GetLatestBlockhash", "Unary helper on the same service."],
      ],
    },
  ],
  cosmos: [
    {
      group: "Node & chain",
      rows: [
        ["status", "Node info, sync state, validator info."],
        ["health", "Liveness check."],
        ["net_info", "Peer information."],
        ["genesis_chunked", "Genesis document (chunked)."],
      ],
    },
    {
      group: "Blocks",
      rows: [
        ["block", "Block by height (latest when omitted)."],
        ["block_by_hash", "Block by hash."],
        ["block_results", "Tx results and events for a block."],
        ["blockchain", "Block headers in a range."],
        ["commit", "Commit / signatures for a height."],
        ["validators", "Validator set at a height."],
      ],
    },
    {
      group: "Transactions & ABCI",
      rows: [
        ["tx", "Transaction by hash."],
        ["tx_search", "Query transactions by event."],
        ["broadcast_tx_sync / _async / _commit", "Broadcast a signed transaction."],
        ["check_tx", "Run CheckTx without broadcasting."],
        ["abci_query", "Raw ABCI query (path + data)."],
        ["abci_info", "Application info."],
      ],
    },
  ],
  "cosmos-rest": [
    {
      group: "Tendermint service",
      rows: [
        ["/cosmos/base/tendermint/v1beta1/node_info", "Node + application version."],
        ["/cosmos/base/tendermint/v1beta1/blocks/latest", "Latest block."],
        ["/cosmos/base/tendermint/v1beta1/blocks/{height}", "Block by height."],
        ["/cosmos/base/tendermint/v1beta1/validatorsets/latest", "Current validator set."],
      ],
    },
    {
      group: "Core modules",
      rows: [
        ["/cosmos/bank/v1beta1/balances/{address}", "All balances for an address."],
        ["/cosmos/bank/v1beta1/supply", "Total supply per denom."],
        ["/cosmos/auth/v1beta1/accounts/{address}", "Account number / sequence."],
        ["/cosmos/staking/v1beta1/validators", "Validators (paginated)."],
        ["/cosmos/staking/v1beta1/delegations/{address}", "Delegations for an address."],
        ["/cosmos/distribution/v1beta1/delegators/{address}/rewards", "Pending rewards."],
        ["/cosmos/gov/v1/proposals", "Governance proposals."],
        ["/cosmos/tx/v1beta1/txs/{hash}", "Transaction by hash."],
        ["/cosmos/tx/v1beta1/txs", "POST — broadcast a signed tx."],
      ],
    },
  ],
  near: [
    {
      group: "Chain",
      rows: [
        ["status", "Chain id, protocol version, sync info."],
        ["block", "Block by finality / height / hash."],
        ["chunk", "Chunk by id or block + shard."],
        ["gas_price", "Gas price at a block."],
        ["network_info", "Peers and producers."],
        ["validators", "Validator set for an epoch."],
      ],
    },
    {
      group: "State & transactions",
      rows: [
        ["query · view_account", "Account balance, storage."],
        ["query · view_access_key / view_access_key_list", "Access keys for an account."],
        ["query · call_function", "Read-only contract call."],
        ["query · view_state", "Raw contract state."],
        ["tx", "Transaction status by hash + sender."],
        ["EXPERIMENTAL_tx_status", "Tx status with receipts."],
        ["broadcast_tx_async / broadcast_tx_commit", "Submit a signed transaction."],
      ],
    },
  ],
  filecoin: [
    {
      group: "Chain",
      rows: [
        ["Filecoin.ChainHead", "Current head tipset."],
        ["Filecoin.ChainGetTipSetByHeight", "Tipset at an epoch."],
        ["Filecoin.ChainGetBlock", "Block header by CID."],
        ["Filecoin.ChainGetMessage", "Message by CID."],
        ["Filecoin.ChainGetParentReceipts", "Receipts for a block's parent messages."],
        ["Filecoin.Version", "Node version + API version."],
      ],
    },
    {
      group: "State & mpool",
      rows: [
        ["Filecoin.StateGetActor", "Actor state (balance, nonce, code)."],
        ["Filecoin.WalletBalance", "Balance for an address."],
        ["Filecoin.StateMinerInfo", "Miner info."],
        ["Filecoin.StateLookupID", "Resolve address → ID address."],
        ["Filecoin.MpoolPush", "Submit a signed message."],
        ["Filecoin.MpoolGetNonce", "Next nonce for an address."],
      ],
    },
    {
      group: "FEVM",
      rows: [["eth_chainId / eth_blockNumber / eth_call / eth_getLogs …", "Standard Ethereum JSON-RPC served by the same node."]],
    },
  ],
  iota: [
    {
      group: "Read",
      rows: [
        ["iota_getChainIdentifier", "Chain identifier."],
        ["iota_getLatestCheckpointSequenceNumber", "Latest checkpoint."],
        ["iota_getCheckpoint", "Checkpoint by sequence number or digest."],
        ["iota_getObject / iota_multiGetObjects", "Object(s) by id."],
        ["iota_getTransactionBlock", "Transaction by digest."],
        ["iota_getProtocolConfig", "Protocol configuration."],
        ["iotax_getBalance / iotax_getAllBalances", "Coin balances for an address."],
        ["iotax_getOwnedObjects", "Objects owned by an address."],
        ["iotax_getReferenceGasPrice", "Reference gas price."],
      ],
    },
    {
      group: "Write",
      rows: [
        ["iota_executeTransactionBlock", "Submit a signed transaction."],
        ["iota_dryRunTransactionBlock", "Simulate a transaction."],
      ],
    },
  ],
  canopy: [
    {
      group: "Routing",
      rows: [
        ["POST /rpc/v1/<route>", "Native query routes — mapped to the node's /v1/<route>."],
        ["POST /adminrpc/v1/<route>", "Native tx routes — mapped to /v1/<route>."],
        ["POST /", "EVM JSON-RPC (eth_*)."],
      ],
    },
  ],
  other: [
    {
      group: "Native",
      rows: [["<chain method set>", "Requests are forwarded unchanged; use the chain's own RPC documentation."]],
    },
  ],
};

// ---------------------------------------------------------------------------
// WebSocket + gRPC streaming
// ---------------------------------------------------------------------------
export const WEBSOCKET = {
  url: GATEWAY.ws,
  chains: ["eth-mainnet", "bsc-mainnet", "story-mainnet", "frax-mainnet"],
  headers: ["x-api-key", "x-chain"],
  example: `import WebSocket from "ws";

const ws = new WebSocket("${GATEWAY.ws}", {
  headers: { "x-api-key": "${KEY_TOKEN}", "x-chain": "eth-mainnet" },
});

ws.on("open", () => {
  ws.send(JSON.stringify({ jsonrpc: "2.0", id: 1, method: "eth_subscribe", params: ["newHeads"] }));
});
ws.on("message", (m) => console.log(m.toString()));`,
};

export const GRPC = [
  {
    name: "Sui gRPC",
    target: GATEWAY.suiGrpc,
    services: "sui.rpc.v2.LedgerService · StateService · MovePackageService · TransactionExecutionService · SubscriptionService (paid)",
    note: "Reflection is off — bring the sui.rpc.v2 protos or use @mysten/sui/grpc. Chain is selected by the service path; only x-api-key metadata is needed.",
    href: "/networks/sui",
  },
  {
    name: "Solana gRPC (Yellowstone)",
    target: GATEWAY.solanaGrpc,
    services: "geyser.Geyser/Subscribe — accounts, slots, transactions, blocks",
    note: "Paid add-on: your plan needs gRPC streams enabled (concurrent-stream cap per organization). Send x-api-key as gRPC metadata.",
    href: "/networks/solana",
  },
];

// ---------------------------------------------------------------------------
// Plans + errors (customer-facing summary; exact quotas live in the dashboard)
// ---------------------------------------------------------------------------
export const PLAN_ROWS = [
  ["JSON-RPC / REST / GraphQL reads", "Included, rate-limited", "Higher RPS & monthly quota"],
  ["Transaction submission", "Included", "Included"],
  ["Archive-class methods", "Included, weighted", "Higher archive quota"],
  ["WebSocket subscriptions", "Included, rate-limited", "Higher RPS & monthly quota"],
  ["gRPC streams (Solana Yellowstone, Sui SubscribeCheckpoints)", "—", "Paid add-on, concurrent-stream cap"],
];

export const HTTP_STATUSES = [
  ["200", "OK — JSON-RPC errors from the chain (e.g. -32601 method not found) still arrive as 200 with an error object."],
  ["400", "Invalid request — missing x-chain, malformed body, or wrong content-type on gRPC."],
  ["401 / 403", "Key missing, invalid, inactive, or the organization has no access to the chain."],
  ["404", "Unknown chain slug."],
  ["429", "Rate limit or quota reached — see the ref code in the message."],
  ["502 / 503 / 504", "Upstream node unreachable, unavailable, or timed out (synthetic JSON-RPC envelope)."],
];

export const NF_CODES = [
  ["NF-AU01", "API key required — set x-api-key or use the /<chain>/<key>/ path form."],
  ["NF-AU02 / AU03 / AU04", "Invalid key format · invalid or revoked key."],
  ["NF-AU05 / AU06", "Invalid key · key inactive or subscription expired."],
  ["NF-IV01", "x-chain header missing."],
  ["NF-AC01 / AC02", "Chain not found · organization does not have access to the chain."],
  ["NF-AC03 / AC04", "Plan lacks gRPC for this chain · streaming needs a paid plan."],
  ["NF-RL01", "Per-second rate limit exceeded — retry in 1 s."],
  ["NF-RL02 / RL03", "Daily · monthly quota reached."],
  ["NF-RL04", "Extra-usage wallet exhausted."],
  ["NF-RL06", "Concurrent gRPC stream limit reached."],
  ["NF-BE01 / BE02 / BE03", "Upstream unavailable · connection error · timeout."],
  ["NF-IN01", "Internal gateway error."],
];

export const ERROR_ENVELOPE = `{
  "jsonrpc": "2.0",
  "error": {
    "code": -32005,
    "message": "[NF] Rate limit exceeded: 25 RPS limit. Retry in 1 second. (ref: NF-RL01)"
  },
  "id": null
}`;

// ---------------------------------------------------------------------------
// SDK snippets (EVM example; swap slug / method for other families)
// ---------------------------------------------------------------------------
export const SDK_SNIPPETS = [
  {
    id: "curl",
    label: "curl",
    code: buildCurl({ slug: "eth", body: '{"jsonrpc":"2.0","id":1,"method":"eth_blockNumber","params":[]}' }),
  },
  {
    id: "fetch",
    label: "JavaScript (fetch)",
    code: `const res = await fetch("${GATEWAY.rpc}", {
  method: "POST",
  headers: {
    "x-api-key": "${KEY_TOKEN}",
    "x-chain": "eth",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({ jsonrpc: "2.0", id: 1, method: "eth_blockNumber", params: [] }),
});
const { result } = await res.json();
console.log(parseInt(result, 16)); // latest block`,
  },
  {
    id: "viem",
    label: "viem",
    code: `import { createPublicClient, http } from "viem";
import { mainnet } from "viem/chains";

// Path form: credentials live in the URL, so any URL-only client works.
const client = createPublicClient({
  chain: mainnet,
  transport: http("${GATEWAY.rpc}/eth/${KEY_TOKEN}"),
});

const block = await client.getBlockNumber();`,
  },
  {
    id: "ethers",
    label: "ethers v6",
    code: `import { JsonRpcProvider } from "ethers";

const provider = new JsonRpcProvider("${GATEWAY.rpc}/eth/${KEY_TOKEN}");
const block = await provider.getBlockNumber();`,
  },
  {
    id: "python",
    label: "Python (web3.py)",
    code: `from web3 import Web3

w3 = Web3(Web3.HTTPProvider(
    "${GATEWAY.rpc}",
    request_kwargs={"headers": {
        "x-api-key": "${KEY_TOKEN}",
        "x-chain": "eth",
    }},
))
print(w3.eth.block_number)`,
  },
];
